import type { TaskStatus } from '@/domains/task/enums';
import type { ClaimType, FtoTaskStep, PatentRiskLevel } from '../enums';
import type { RETRIEVAL_STRATEGY } from '../constants';

/**
 * 技术特征
 * @description 从用户输入的技术要点中提取的技术特征
 */
export interface TechFeature {
    /**
     * 技术特征名称
     */
    name: string;
    /**
     * 技术特征原因
     */
    reason: string;
    /**
     * 技术特征关键词
     */
    keywords: string[];
    /**
     * 技术特征得分
     */
    score: number;
    /**
     * 是否选中
     */
    selected: boolean;
    /**
     * 是否为核心技术特征
     */
    flagged?: boolean;
    /**
     * 是否为用户添加
     */
    manualAdded?: boolean;
    /**
     * 技术特征组成部分
     */
    components: string[];
}


/**
 * 包含匹配特征的专利信息
 */
export interface PatentProfileWithMatchedTechFeature {
    /**
     * 对比文献类型
     */
    comparativeLiteratureType: 'X' | 'Y' | 'A';
    /**
     * 专利 id
     */
    patentId: string;
    /**
     * 匹配特征
     */
    matchedTechFeatures: MatchedTechFeature[];
    /**
     * 是否选中
     */
    selected: boolean;
    /**
     * 是否最相似
     */
    mostSimilar: boolean;
    /**
     * 提取专利特征所用到的语言
     */
    lang: string;
    /**
     * 对比结论
     */
    comparisonConclusion: {
        en: string;
        zh: string;
    };

    /**
     * 等同独权数量
     */
    equivalentIndependentClaimNum?: number;
    /**
     * 独权数量
     */
    independentClaimNum?: number;
    /**
     * 等同从权数量
     */
    equivalentDependentClaimNum?: number;
    /**
     * 从权数量
     */
    dependentClaimNum?: number;
    /**
     * 风险等级
     */
    riskLevel?: PatentRiskLevel;
    /**
     * 权利要求等同信息
     */
    claimEquivalentInfo?: {
        claimNum: number;
        infringement: boolean;
    }[];
    /**
     * 权利要求信息
     */
    claims?: {
        claimNum: number;
        claimText: string;
    }[];

    /**
     * 20250910后，这个含义有变化
     * 是否为用户添加
     * true表示这个专利是非检索结果中出现的专利
     * 如果该专利是检索结果中出现的专利，则该字段为 false
     * 不管是否是手动添加还是从workspace加入的
     */
    userAdded?: boolean;

    /**
     * 从workspace加入的专利的会有这个文件夹 id
     */
    folderId?: string;
    /**
     * 从workspace加入的专利的会有这个字段
     */
    workspaceAdded?: boolean;
}

/**
 * 技术特征同义词
 */
export interface TechFeatureSynonym {
    /**
     * 技术特征
     */
    techFeature: string;
    /**
     * 技术特征翻译
     * @description 用途未知
     */
    translation: string;
    /**
     * 同义词
     */
    synonym: string[];
    /**
     * 同义词得分
     */
    synonymScore: number[];
    /**
     * @description 用途未知
     */
    words: Omit<TechFeatureSynonym, 'words'>[];
}

/**
 * 参数名称
 */
export enum Parameter {
    FEATURES = 'features',
    IPC = 'ipc',
    CPC = 'cpc',
}

/**
 * 检索结果
 */
export interface RetrievalResult {
    /**
     * 检索思路
     */
    idea: string;
    /**
     * 检索表达式
     */
    searchExpress: string;
    /**
     * 检索结果
     */
    patents: {
        patentId: string;
        score?: number;
    }[];
    /**
     * 检索策略参数
     */
    strategyParameters: {
        name: Parameter;
        value: string[];
    }[];
    /**
     * 检索是否完成
     * @description 检索+技术特征识别完成
     */
    finished: boolean;
    /**
     * 下一步计划
     */
    nextPlan: string;
}

/**
 * 检索步骤
 */
export interface RetrievalStep {
    /**
     * 检索步骤 id
     */
    id: string;
    /**
     * 检索步骤名称
     */
    name: RETRIEVAL_STRATEGY;
    /**
     * 检索步骤是否运行中
     */
    running: boolean;
}

/**
 * 检索进度状态
 */
export interface RetrievalProgressState {
    /**
     * 检索耗时
     */
    timeSpend: number;
    /**
     * 检索节约时间
     */
    timeSaved: number;
    /**
     */
    steps: RetrievalStep[];
    /**
     * 检索结果
     */
    result: Map<RETRIEVAL_STRATEGY, RetrievalResult>;
}

/**
 * @description 当前匹配专利排行榜状态
 */
export interface CurrentMatchedPatentState {
    /**
     * 当前匹配专利排行榜
     */
    ranking: PatentProfileWithMatchedTechFeature[];
    /**
     * 手动添加的匹配专利
     */
    manualAdded: PatentProfileWithMatchedTechFeature[];
    /**
     * 额外搜索的新专利
     */
    extraAdded: PatentProfileWithMatchedTechFeature[];
}

/**
 * 检索要素及同义词
 */
export interface RetrievalElementsAndSynonyms {
    /**
     * 检索要素
     */
    element: string;
    /**
     * 同义词
     */
    synonyms: string[];
}

/**
 * 检索策略
 */
export interface RetrievalStrategy {
    /**
     * 检索策略名称
     * @description 检索方法：语义检索、分类号检索、渐进式检索
     */
    name: string;
    /**
     * 检索策略内容
     * @description 检索式
     */
    content: string;
}

/**
 * 检索结果报告状态
 */
export interface RetrievalResultReportState {
    /**
     * 检索结果报告标题
     */
    title: string;
    /**
     * 用户原始输入
     * @description 创建任务时的原始输入文本
     */
    userInput: string;
    /**
     * 用户输入的（或 AI 总结用户输入）技术要点
     */
    techEssential: string;
    /**
     * 用户选择的专利受理局
     */
    country: string[];
    /**
     * 法律状态
     */
    legalStatus: number[];
    /**
     * 包含公司
     */
    includeCompany: string[];
    /**
     * 排除公司
     */
    excludeCompany: string[];
    /**
     * 申请日期开始
     */
    apdStart: string
    /**
     * 申请日期结束
     */
    apdEnd: string
    /**
     * 检索技术特征及关键词
     * @description 技术特征
     */
    techFeatures: TechFeature[];
    /**
     * 技术特征阈值
     */
    threshold: number;
    /**
     * 技术特征与对比文献
     */
    techFeaturesWithComparedPats: {
        patentId: string;
        publicScore: number;
        relatedPara: string;
        pn: string;
    }[];
    /**
     * 新颖性评述
     */
    noveltyReview: string[];
    /**
     * 创造性评述
     */
    creativityReview: string[];
    /**
     * 创建者
     */
    creator: string;
    /**
     * 生成时间
     */
    generatedTime: number;
    /**
     * 搜索数量
     */
    searchQty: number;
    /**
     * 对比数量
     */
    comparativeQty: number;
    /**
     * 最接近的对比文献数量
     */
    nearestQty: number;
    /**
     * 次接近的对比文献数量
     */
    secondaryApproachQty: number;
    /**
     * 搜索范围
     */
    searchScope: {
        en: string;
        zh: string;
        jp: string;
    };
    /**
     * 副标题
     */
    header: {
        en: string;
        zh: string;
        jp: string;
    };
}

/**
 * 专利匹配到的技术特征
 */
export interface MatchedTechFeature {
    /**
     * 技术特征文本 {@link TechFeature.name}
     */
    techFeature: string;
    /**
     * 对比技术特征文本
     * @description 专利中的技术特征的文本
     */
    comparisonFeature: string;
    /**
     * 相似度得分？？？
     */
    score: number;
    /**
     * 特征匹配得分？？？
     */
    publicScore: number;
    /**
     * 是否相似
     */
    similar: boolean;
    /**
     * 权利要求编号
     */
    claimNum?: number;
    /**
     * 权利要求类型
     */
    claimType?: ClaimType;
}

/**
 * 特征提取结果
 */
export interface PatentFeatureExtractorResult {
    /**
     * 专利特征
     */
    features: MatchedTechFeature[];
    /**
     * 特征匹配得分？？？
     */
    publicScore: number;
}

/**
 * 技术特征与对比文献结果
 */
export interface TechFeatureWithComparedPats {
    /**
     * 专利号
     */
    patentNumber: string;
    /**
     * 本发明技术要点
     */
    techAbstract: string;
    /**
     * 本发明公开度
     * @description 0-100
     */
    score: number;
    /**
     * 对比文献公开情况？？？
     */
    result: unknown[];
}

/**
 * 专利新颖性检查任务状态
 */
export interface FtoReportTaskStatus {
    /**
     * 任务状态
     */
    taskStatus: TaskStatus;
    /**
     * 任务步骤
     */
    taskStep: FtoTaskStep;
    /**
     * 最后更新时间
     * @description 按用户设置时区格式化
     */
    lastUpdateTime: string;
    /**
     * 查新输入
     */
    userInput: string;
    /**
     * 输入语言（大写字母）
     * @description 用户查新原始输入（或经总结）文本的语言,
     * @example 'CN' | 'EN'
     */
    inputLanguage: string;

    /**
     * 是否为历史数据
     * @description 截止到PBI-8783，历史数据为老数据
     */
    isHistoryData?: boolean;
}

export interface WorkspaceInfo {
    spaceId: string;
    folderId: string;
    folderPath: string;
    batchSize: number;
}
